import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useSelectionSelector } from "../../store/selection";
import { LegendLayerConfig, MapLibreLayerSpecification, MapLayerConfig } from "../../types"; // Adjust the import path as necessary
import { apiFetch } from "../../utilities/api";
import { transformToMapLibreLayer } from "../../utilities/transformLayerConfigs";
import { enqueueUniqueSnackbar } from "../../utilities/snackbarUtils";
import { isFulcrumLayer } from "../../utilities/fulcrumLibService";
import { validateCloudFrontCookies, isCloudFrontAuthError } from "../../utilities/cloudFrontAuth";
import { isEnvCdnUrl } from "../../utilities/cdnUtils";
import { useAuthSelector } from "../../store/auth";
import { authTokenService } from "../../utilities/authTokenService";
import { useLayerSelector } from "../../store/layer";

export const useMapLibreLayers = () => {
  // Using Zustand store hooks instead of context for layer context
  //   made a copy of useMapLibreLayers as useMapLibreLayersUpdated to avoid confusion during edits
  // Other than store hooks, rest of the code is same as before
  // Other Context need to be replace later thats why making a copy for now
  // As this previous version is used in legacy map component which is still in use.
  const { layers, layerRefreshTrigger } = useLayerSelector();

  const { basicAuthHeader, oAuthHeader, user: profile } = useAuthSelector();
  const [mapLibreLayers, setMapLibreLayers] = useState<MapLibreLayerSpecification[]>([]);
  const [mapLibreLayersReady, setMapLibreLayersReady] = useState(false);
  const { currentFilter, filterArea, selectedPrimaryFilterEntity } = useSelectionSelector();
  const loadedFiltersRef = useRef<Set<string>>(new Set());
  const isLoadingRef = useRef(false);
  const [errorLayers, setErrorLayers] = useState<Set<string>>(new Set());
  const { enqueueSnackbar } = useSnackbar();
  const { accessToken } = authTokenService();

  // useRef to track the previous layers dependency to detect significant changes
  const prevLayersRef = useRef<MapLayerConfig[]>();

  // Effect to handle layer refresh triggers
  useEffect(() => {
    // Check if any layers need to be refreshed
    const layersToRefresh = Object.keys(layerRefreshTrigger).filter((layerId) => layerRefreshTrigger[layerId] > 0);

    if (layersToRefresh.length === 0) return;

    // Since layers are loaded together in combined cache entries,
    // we need to clear all cache to refresh specific layers
    // This is acceptable since layer loading is reasonably fast
    if (layersToRefresh.length > 0) {
      loadedFiltersRef.current.clear();
      setMapLibreLayersReady(false);
      isLoadingRef.current = false; // Reset loading flag to allow reload
    }
  }, [layerRefreshTrigger]);

  useEffect(() => {
    // Reset internal states if the core (non-spatial) layer definition changes.
    // Spatial layers (spatial-*) are rendered directly in MapComponent via react-map-gl
    // Source/Layer and must NOT trigger a reset here — doing so would remove all map
    // sources and freeze the map when a dataset is selected.
    if (prevLayersRef.current !== layers) {
      const prevNonSpatialIds = (prevLayersRef.current ?? [])
        .filter((l) => !l.id.startsWith("spatial-"))
        .map((l) => l.id)
        .sort()
        .join(",");
      const currNonSpatialIds = layers
        .filter((l) => !l.id.startsWith("spatial-"))
        .map((l) => l.id)
        .sort()
        .join(",");

      if (prevNonSpatialIds !== currNonSpatialIds) {
        console.debug("Core (non-spatial) layers changed, resetting mapLibreLayers state.");
        setMapLibreLayers([]);
        setMapLibreLayersReady(false);
        setErrorLayers(new Set());
        loadedFiltersRef.current = new Set();
        isLoadingRef.current = false;
      }
    }
    // Update the ref for the next render
    prevLayersRef.current = layers;

    // Use either the structured entity name or fallback to the legacy filterArea
    const activeFilterValue = selectedPrimaryFilterEntity?.name || filterArea;
    // Exclude spatial-* layers from depsKey — they're rendered separately
    const layerIds = layers
      .filter((l) => !l.id.startsWith("spatial-"))
      .map((l) => l.id)
      .sort()
      .join(",");
    const depsKey = `${layerIds}-${!!basicAuthHeader}-${!!oAuthHeader}-${activeFilterValue || "initial"}`;

    // Skip if no OAuth header is available yet
    if (layers.some((layer) => layer.useOAuth) && !oAuthHeader) {
      return;
    }

    if (loadedFiltersRef.current.has(depsKey)) return;
    if (isLoadingRef.current) return;

    // Set loading state at the beginning of the fetch process
    setMapLibreLayersReady(false);

    const fetchAndAddAuthToLayers = async () => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      // Note: errorLayers is reset above if layers changed, otherwise errors persist per session

      try {
        const transformedLayers: MapLibreLayerSpecification[] = [];

        for (const layer of layers.filter(
          // Exclude terrain layers and spatial-* layers (both rendered directly
          // in MapComponent via react-map-gl Source/Layer)
          (layer): layer is LegendLayerConfig => layer.type !== "raster-dem" && !layer.id.startsWith("spatial-")
        )) {
          try {
            // Handle Fulcrum layers
            if (isFulcrumLayer(layer)) {
              try {
                // Use the service approach
                // @ts-ignore - TS won't recognize the formId property
                const formId = layer.formId;

                // Use the direct API approach without requiring fulcrum-app library
                const { fetchFulcrumRecordsAsGeoJSON, createGeoJsonLayerConfig } = await import("../../utilities/fulcrumLibService");

                // Fetch GeoJSON with proper authentication
                const geoJsonData = await fetchFulcrumRecordsAsGeoJSON(formId, profile);

                // Create a properly typed GeoJSON layer config using the helper function
                const geoJsonLayer = createGeoJsonLayerConfig(layer, geoJsonData);

                // Process as a standard GeoJSON layer
                const mapLibreLayer = transformToMapLibreLayer(geoJsonLayer, geoJsonData);

                if (mapLibreLayer) {
                  transformedLayers.push(mapLibreLayer);
                }

                continue; // Skip to next layer
              } catch (error: any) {
                // Type the error as any to handle error.message safely
                console.error(`Error with data layer ${layer.id}:`, error);
                setErrorLayers((prev) => new Set(prev).add(layer.id));

                // More user-friendly error messages for common Fulcrum API issues
                let errorMessage = `Failed to load ${layer.id}`;
                if (error?.message?.includes("Failed to fetch") || error?.message?.includes("503") || error?.message?.includes("CORS")) {
                  errorMessage = `${layer.id} temporarily unavailable (API issue)`;
                } else if (error?.message) {
                  errorMessage = `Failed to load ${layer.id}: ${error.message}`;
                }

                enqueueUniqueSnackbar(enqueueSnackbar, errorMessage, {
                  variant: "warning", // Changed from 'error' to 'warning' for API issues
                  autoHideDuration: 5000
                });
                continue; // Skip this layer
              }
            }

            let data;
            const headers = {};

            if (layer.useBasicAuth && basicAuthHeader) {
              Object.assign(headers, basicAuthHeader);
            } else if (layer.useOAuth && oAuthHeader) {
              Object.assign(headers, oAuthHeader);
            }

            // @ts-ignore
            if (Object.keys(headers).length > 0 || layer.filters) {
              const options: RequestInit = { headers };

              if ("filters" in layer && layer.filters && layer.filters.length > 0) {
                options.method = "POST";
                options.headers = {
                  ...options.headers,
                  "Content-Type": "application/json"
                };
                options.body = currentFilter ? JSON.stringify({ filter: currentFilter }) : JSON.stringify({ filter: layer.filters[0] });
              }

              try {
                if (layer.useOAuth) {
                  if (!oAuthHeader) {
                    continue;
                  }

                  data = await apiFetch(layer.url, {
                    ...options,
                    credentials: "include",
                    headers: options.headers as Record<string, string>
                  });
                } else {
                  const response = await fetch(layer.url, options);
                  if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                  }
                  data = await response.json();
                }
              } catch (error) {
                console.warn(`Failed to fetch layer ${layer.id}:`, error);
                setErrorLayers((prev) => new Set(prev).add(layer.id));
                enqueueUniqueSnackbar(enqueueSnackbar, `Failed to load layer: ${layer.id}`, {
                  variant: "warning",
                  autoHideDuration: 3000
                });
                continue; // Skip this layer and continue with others
              }
            } else {
              // For layers without OAuth/Basic auth, check if it's a CDN URL that needs cookies
              const cdnUrl = layer.url && typeof layer.url === "string" && isEnvCdnUrl(layer.url);
              // @ts-ignore - Allow for optional useCookies property on layer config
              const needsCredentials = layer.useCookies || cdnUrl;

              if (needsCredentials && typeof layer.url === "string") {
                try {
                  const response = await fetch(layer.url, {
                    credentials: "include" // Include cookies for CDN authentication
                  });

                  if (!response.ok) {
                    console.warn(`CDN layer fetch failed for ${layer.id}: ${response.status} ${response.statusText}`);

                    // Use proper CloudFront authentication error detection
                    if (response.status === 403) {
                      const errorText = await response.text().catch(() => "Unknown error");
                      console.warn(`403 error details for ${layer.id}:`, errorText.substring(0, 200));

                      // Use the robust CloudFront error detection
                      const isCloudFrontError = isCloudFrontAuthError(response, errorText);

                      if (isCloudFrontError) {
                        // Validate CloudFront cookies properly
                        const cookieValidation = validateCloudFrontCookies();

                        console.warn(`CloudFront authentication issue detected for ${layer.id}:`, {
                          cookieValidation,
                          errorSnippet: errorText.substring(0, 100)
                        });

                        // Only log warning - don't automatically logout users
                        if (!cookieValidation.isValid) {
                          console.error(`CloudFront authentication problem for ${layer.id}: ${cookieValidation.details}`);
                          // Could potentially show a non-intrusive notification here
                          // but don't automatically log out the user
                        }
                      } else {
                        console.warn(`Non-CloudFront 403 error for ${layer.id} - likely server/permission issue`);
                      }
                    }

                    // Don't throw error for failed layers - just mark as error and continue
                    throw new Error(`CDN layer ${layer.id} failed: ${response.status} ${response.statusText}`);
                  }

                  data = await response.json();
                } catch (error) {
                  console.warn(`Failed to fetch CDN layer ${layer.id}:`, error);
                  setErrorLayers((prev) => new Set(prev).add(layer.id));

                  // Provide specific error message for expired cookies
                  let errorMessage = `Failed to load CDN layer: ${layer.id}`;
                  if (error instanceof Error && error.message.includes("CloudFront authentication expired")) {
                    errorMessage = `Session May Be Expired - try logging out and back in to refresh access`;
                  }

                  enqueueUniqueSnackbar(enqueueSnackbar, errorMessage, {
                    variant: "warning",
                    autoHideDuration: 5000 // Longer duration for important messages
                  });
                  continue; // Skip this layer and continue with others
                }
              } else {
                // For other URLs, use the URL directly (no fetch needed)
                data = layer.url;
              }
            }

            // Take the layer definition, and its data that we have fetched with our oAuth etc, and transform it into a MapLibre Layer Specification ready to load on the map

            const mapLibreLayer = transformToMapLibreLayer(layer, data);

            if (mapLibreLayer) {
              transformedLayers.push(mapLibreLayer);
            }
          } catch (layerError) {
            console.warn(`Error processing layer ${layer.id}:`, layerError);
            setErrorLayers((prev) => new Set(prev).add(layer.id));
            continue; // Skip this layer and continue with others
          }
        }

        loadedFiltersRef.current.add(depsKey);

        setMapLibreLayers(transformedLayers);
        setMapLibreLayersReady(true);
      } catch (error) {
        console.error("Error processing layers:", error);
        enqueueSnackbar("Error loading map layers", {
          variant: "error",
          autoHideDuration: 4000
        });
      } finally {
        isLoadingRef.current = false;
      }
    };

    if (!accessToken) {
      // If no token, ensure we are in a clean state
      setMapLibreLayers([]);
      setMapLibreLayersReady(false);
      setErrorLayers(new Set());
      loadedFiltersRef.current = new Set();
      return;
    }

    fetchAndAddAuthToLayers();
  }, [layers, basicAuthHeader, oAuthHeader, filterArea, selectedPrimaryFilterEntity, layerRefreshTrigger]);

  // Separate effect for filter changes
  useEffect(() => {
    if (!currentFilter || !mapLibreLayersReady) return;
    // Handle filter changes here when needed
  }, [currentFilter, mapLibreLayersReady]);

  // Handle user logout by clearing layer processing state
  useEffect(() => {
    if (!profile) {
      console.log("[useMapLibreLayers] User logged out, clearing layer processing state");
      isLoadingRef.current = false; // Stop any ongoing processing
      setErrorLayers(new Set()); // Clear error layers
    }
  }, [profile]);

  return {
    mapLibreLayers,
    mapLibreLayersReady,
    errorLayers
  };
};
